import React, { useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import cx from 'classnames';
import { makeStyles } from '@material-ui/core';
import app from 'new-ui/app';
import { useIsMobileResolution } from 'utils/hooks/useIsMobileResolution';
import { QUESTION_ID } from 'modules/questionnaire/questionIds';
import {
  SINGLE_OPTION_SELECT, MONTH_PICKER, BIOMARKERS, SUB_STAGE_SELECT,
  // TREATMENT_HISTORY,
} from 'modules/questionnaire/questionTypes';
import QuestionRenderer from './QuestionRenderer';
import QuestionContainer from './QuestionContainer';
import QuestionInfo from './QuestionInfo';
import { ResetAnswer } from './ResetAnswer';
import NextButton from './NextButton';
import { checkQuestionAnswer } from '../functions/checkQuestionAnswer';
import { setFieldValue, nextStep } from '../actions';
import { useQuestionnaireData } from '../context';

const useStyles = makeStyles((theme) => ({
  nextButton: {
    display: 'flex',
    justifyContent: 'center',
  },
  formMaxWidth: {
    maxWidth: 330,
    [theme.breakpoints.down('xs')]: {
      maxWidth: 'unset',
    },
  },
  questionContainerForm: {
    width: '100%',
    margin: ({ provider }) => (provider ? '0px auto' : 'unset'),
  },
  questionContainerWrapper: {
    minHeight: ({ isMobile }) => (isMobile ? '70vh' : 'unset'),
  },
}));

const Question = (props) => {
  const {
    handleSubmit,
    question,
    forwardedRef,
    setAbilityToChangePrevBtnEvent,
    setAbilityToChangeSkipBtnEvent,
  } = props;
  const {
    files, form, geoLocation, provider,
  } = useSelector((state) => ({
    geoLocation: state.geo_location,
    form: state.questionnaire.form,
    files: state.questionnaire.files,
    provider: state?.appConfiguration?.providerSpecific?.code,
  }));
  const isMobile = useIsMobileResolution();
  const { info, id: questionId } = question;
  const dispatch = useDispatch();
  const {
    embedded, beforeNextStepAction, changeStepAction, showNextButton = true,
  } = useQuestionnaireData();
  const isQuestionAnswered = checkQuestionAnswer(question, { form, files, geoLocation });
  const showInfoTitle = info && info.title;
  const classes = useStyles({ provider, isMobile });
  const shouldNextButtonBeShown = !!((
    (!isMobile && ![SINGLE_OPTION_SELECT, SUB_STAGE_SELECT].includes(question.type))
      || question.type === BIOMARKERS
      // || question.type === TREATMENT_HISTORY
  ) && (isQuestionAnswered && showNextButton) && !provider);

  const onChange = useCallback(
    (value) => {
      dispatch(
        setFieldValue({
          id: questionId,
          value,
        }),
      );
      if (question.type === MONTH_PICKER) {
        dispatch(nextStep({ beforeNextStepAction, changeStepAction }));
      }
    },
    [dispatch, questionId, question, beforeNextStepAction, changeStepAction],
  );
  const resetAnswer = useCallback(() => {
    dispatch(
      setFieldValue({
        id: questionId,
        value: null,
      }),
    );
  }, [dispatch, questionId]);

  const handleNextButtonClick = useCallback(() => {
    dispatch(nextStep({ beforeNextStepAction, changeStepAction }));
  }, [dispatch, beforeNextStepAction, changeStepAction]);

  useEffect(() => {
    if (embedded) {
      return;
    }
    app.intercom.bootPublic();
    if (!['disease_status', 'treatments'].includes(questionId)) return;

    setTimeout(() => {
      app.intercom.sendEvent(`q-${questionId.replaceAll('_', '-')}`);
    }, 100);
  }, [questionId, embedded]);

  return (
    (
      <QuestionContainer
        className={classes.questionContainerWrapper}
        isHeightResizeable={isMobile}
        questionId={questionId}
        embedded={embedded}
      >
        <form
          onSubmit={handleSubmit}
          className={cx(classes.questionContainerForm, { [classes.formMaxWidth]: question.type === MONTH_PICKER })}
        >
          <QuestionRenderer
            form={form}
            question={question}
            value={form[questionId]}
            onChange={onChange}
            shouldNextButtonBeShown={shouldNextButtonBeShown}
            handleNextButtonClick={handleNextButtonClick}
            setAbilityToChangePrevBtnEvent={setAbilityToChangePrevBtnEvent}
            setAbilityToChangeSkipBtnEvent={setAbilityToChangeSkipBtnEvent}
            forwardedRef={forwardedRef}
          />
          {!!question.answerResetable && isQuestionAnswered && <ResetAnswer handleReset={resetAnswer} />}
          <div
            className={cx({
              [classes.nextButton]:
              QUESTION_ID.treatments.questionId || question.id === QUESTION_ID.procedures.questionId,
            })}
          >
            {shouldNextButtonBeShown && !embedded ? <NextButton /> : null}
          </div>
        </form>

        {showInfoTitle && <QuestionInfo isMobile={isMobile} title={info.title} content={info.content} />}
      </QuestionContainer>
    )
  );
};

Question.propTypes = {
  question: PropTypes.object.isRequired,
  setAbilityToChangePrevBtnEvent: PropTypes.func.isRequired,
  setAbilityToChangeSkipBtnEvent: PropTypes.func.isRequired,
  forwardedRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.any }),
  ]),
  handleSubmit: PropTypes.func.isRequired,
};

export default Question;
